/**
 * WSH — URL Param → Klaviyo Profile Property Writer (Path A transport)
 *
 * Reads allowlisted URL params, extracts the Klaviyo _kx click token from
 * the URL, and POSTs to the WSH CMS server-side proxy which does the
 * profile PATCH + event emit via Klaviyo's Private API.
 *
 * Path A shipped 2026-09-05 replaces the earlier Phase B transport
 * (Shopify.analytics.publish + window.klaviyo.track + _learnq.push) —
 * all three of those channels are structurally dead on this store per
 * Layer 3 durable D1 (Klaviyo Web Pixel not registered in Shopify Web
 * Pixels manager; window.klaviyo write methods return but never flush).
 *
 * The A2 acknowledgement notice still renders on both emit + skip paths.
 * Notice copy is deliberately an acknowledgement ("Logic Pro it is") not
 * a write-completion claim, so it stays honest whether the CMS write
 * actually landed or the endpoint 5xx'd silently.
 *
 * Payload contract with the CMS endpoint:
 *   {
 *     "param_name":  "daw",                            (URL param key)
 *     "raw_key":     "logic",                           (allowlist key)
 *     "kx":          "FXMJ8IgWnKGYiNCLYtO9KA.depndL",   (Klaviyo click token from URL)
 *     "landing_url": "https://www.wesoundhuman.com/pages/daw-packs#logic",
 *     "source":      "url_param_writer"
 *   }
 * Same payload shape for the PP-1a answer params (mood, wsh_corner, wsh_missing, wsh_last, wsh_set, wsh_creator).
 *
 * Server resolves the property_name + canonical property_value from the
 * allowlist server-side (never trusts client's raw_key blind). Endpoint
 * URL uses /wsh/* NOT /api/* because Laravel's built-in HandleCors
 * middleware is scoped to api/* with wildcard origins on the CMS side.
 *
 * Identifier policy (per §11 decision): _kx URL-param only, no cookie-
 * anonymous_id fallback. If no _kx present → silent skip (notice still
 * renders). Klaviyo's onsite tracker will identify from the cookie on
 * a future click that DOES carry _kx.
 *
 * Idempotence: guards against double-init on repeat script load.
 *
 * Brief: reference/WSH_URL_Param_Profile_Property_Brief.md +
 *        reference/WSH_Step2_Opener_DAW_Casing_PathA.md
 */
(function () {
  if (window.__wshProfilePropertyWriterInit) return;
  window.__wshProfilePropertyWriterInit = true;

  var CMS_ENDPOINT = 'https://cms.wesoundhuman.com/wsh/klaviyo-profile-property';
  var SOURCE = 'url_param_writer';

  function extractKxFromUrl() {
    // Klaviyo appends _kx to tracked email links. Under Path A this is the
    // only server-recognised identifier (§11 decision: no cookie-anon
    // fallback). Returns the raw token string or null if absent.
    var params = new URLSearchParams(window.location.search);
    return params.get('_kx') || null;
  }

  function emit(paramName, rawKey, kx, landingUrl) {
    var payload = {
      param_name: paramName,
      raw_key: rawKey,
      kx: kx,
      landing_url: landingUrl,
      source: SOURCE,
    };
    try {
      // Fire-and-forget. Server does the Klaviyo PATCH + event emit.
      // Any failure (CORS-block, network, 4xx/5xx) is silently swallowed
      // — the notice is the user-facing acknowledgement.
      // `keepalive: true` protects the request against page navigation
      // in case a later brief chains a nav after this call.
      fetch(CMS_ENDPOINT, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(function () { /* swallow */ });
    } catch (_) { /* swallow */ }
  }

  function stripParamsFromUrl(paramNamesToStrip) {
    if (!paramNamesToStrip.length) return;
    var url = new URL(window.location.href);
    var changed = false;
    paramNamesToStrip.forEach(function (name) {
      if (url.searchParams.has(name)) { url.searchParams.delete(name); changed = true; }
    });
    if (changed) {
      try { history.replaceState({}, '', url.toString()); } catch (_) {}
    }
  }

  function run() {
    var allowlist = window.wshProfileAllowlist;
    if (!allowlist || typeof allowlist !== 'object') return;

    var params = new URLSearchParams(window.location.search);
    var kx = extractKxFromUrl();
    var paramsToStrip = [];

    Object.keys(allowlist).forEach(function (paramName) {
      if (!params.has(paramName)) return;
      var rawValue = params.get(paramName);
      var entry = allowlist[paramName];
      if (!entry || typeof entry !== 'object') return;
      var propertyName = entry.property_name;
      var values = entry.values || {};
      // "slug" = any lower-case key; the CMS checks it against its own list (PP-1a wsh_corner).
      var isSlug = values === 'slug';
      if (isSlug ? !/^[a-z0-9_]{1,60}$/.test(rawValue) : !(rawValue in values)) {
        // Silent no-op on invalid value. Do NOT strip — attacker gets no oracle.
        return;
      }
      var propertyValue = isSlug ? rawValue : values[rawValue];
      var landingUrlPre = window.location.href;
      var writeStatus;

      if (kx) {
        var urlForEvent = new URL(landingUrlPre);
        urlForEvent.searchParams.delete(paramName);
        emit(paramName, rawValue, kx, urlForEvent.toString());
        writeStatus = 'emitted';
      } else {
        writeStatus = 'skipped_no_identifier';
        try {
          console.info('[wsh-profile-property-writer] skip: no identifier for',
            paramName + '=' + rawValue, '(no _kx URL param)');
        } catch (_) {}
      }

      paramsToStrip.push(paramName);

      try {
        document.dispatchEvent(new CustomEvent('wsh:profile-property:acknowledged', {
          detail: {
            param_name: paramName,
            property_name: propertyName,
            property_value: propertyValue,
            raw_key: rawValue,
            write_status: writeStatus,
          },
        }));
      } catch (_) {}
    });

    stripParamsFromUrl(paramsToStrip);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
